// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPrice from "@stanion/Overrides/Components/ProductPrice";
import ProductQuantityOrdered, {
    ProductQuantityOrderedStyles,
} from "@stanion/Overrides/Components/ProductQuantityOrdered";
import ListView from "@stanion/XDL/Icons/ListView";
import { color, mergeWithCss } from "@stanion/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    // XNG-Changes
    showCompare: boolean;
    showAvailability?: boolean;
    extendedStyles?: ProductListActionsStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productSettings: getSettingsCollection(state).productSettings,
    // Compare & AddToList section
    canShowActionsSection: ownProps.showAddToList || ownProps.showCompare,
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: ProductQuantityOrderedStyles;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    // XNG-Changes
    productImageStyles?: ProductListProductImageStyles;
    availabilityStyles?: ProductAvailabilityStyles;
    availabilityContainer?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 10px;
            `,
            inputSelect: css`
                height: 30px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
            margin-bottom: 16px;
        `,
    },
    addToCartButton: {
        sizeVariant: "small",
        css: css`
            width: 80%;
            margin-left: 16px;
        `,
    },
    quantityOrdered: {
        wrapper: {
            css: css`
                display: flex;
                border: 1px solid ${color("common.ui5")};
                padding: 0 9px;
                max-height: 30px;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            min-height: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        `,
    },
    price: {
        price: {
            priceText: { weight: "bold", size: 18, lineHeight: "26px" },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 15,
                    lineHeight: "17px",
                    css: css`
                        white-space: nowrap;
                    `,
                },
            },
            signInText: {
                size: 12,
                lineHeight: "13px",
                css: css`
                    span {
                        font-size: 12px;
                        line-height: 13px;
                    }
                `,
            },
        },
    },
    addToListLink: {
        link: {
            typographyProps: {
                size: 12,
                lineHeight: "13px",
            },
            icon: {
                position: "left",
                iconProps: {
                    src: ListView,
                    size: 12,
                    color: "common.ui8",
                },
            },
        },
    },
};

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    canShowActionsSection,
    showCompare,
    // XNG-Changes
    showAvailability,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeWithCss(actionsStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                        cantSeePriceMessagePLP={true}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            {productSettings.showInventoryAvailability && showAvailability && productSettings.canSeePrices && (
                <StyledWrapper {...styles.availabilityContainer}>
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                </StyledWrapper>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            <StyledWrapper {...styles.addToCartWrapper}>
                <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
            </StyledWrapper>

            {canShowActionsSection && (
                <StyledWrapper {...styles.addToListWrapper}>
                    {showAddToList && (
                        <ProductAddToListLink
                            data-test-selector={`actionsAddToList${productContext.product.id}`}
                            extendedStyles={styles.addToListLink}
                        />
                    )}

                    <ProductListProductImage
                        showImage={false}
                        showCompare={showCompare}
                        extendedStyles={styles.productImageStyles}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
